/**
 * Changing a row, from the tab that changed it.
 *
 * The realtime handlers cover a row somebody *else* edited; this is the other
 * side, and it is a different job. The acting tab is excluded from its own row
 * broadcasts, so nothing is coming back over the socket to tell it what it did:
 * the edit has to appear immediately, be sent, and then be reconciled with what
 * the server made of it.
 */
import { getApi, unwrap } from '@/api/client'
import type { RowData } from '@/api/types'
import { canUpdateOptimistically } from './optimism'
import { operationsFor } from './rowEvents'
import { splitHold } from './rowHold'
import { rowSettled, rowUpdated } from './rowLifecycle'
import { placeholderOrders } from './rowSort'
import { lifecycleContextFor, type RowWindowMember } from './rowWindowRegistry'
import { indexOfRow, rowAt } from './rowWindow'
import { applyRowOperations } from './rowWindowStore'
import { createTaskQueue } from './taskQueue'

/**
 * One queue for the whole application, keyed by row id.
 *
 * Module-level because the key is the row, not the grid: the same row can be
 * edited from a grid and a row modal at once, and two queues would let those
 * two overlap -- which is the thing the queue exists to prevent.
 */
const queue = createTaskQueue()

/**
 * What the batch endpoint answers with, narrowed to what is read.
 *
 * `unknown[]` and not `RowData[]`, for the reason every row payload is
 * hand-typed: the backend synthesises one example row serializer describing a
 * fictional table, so the generated type is a real type of a table nobody has.
 * The cast to `RowData` happens where the rows are used, which is the one
 * boundary the plan allows it at.
 */
interface BatchUpdateResponse {
  items?: unknown[]
  metadata?: {
    cascade_update?: { rows?: unknown[] }
  }
}

export interface UpdateRowOptions {
  /** What the reader is searching for, which is part of whether a row matches. */
  searchTerm?: string
  /**
   * Whether the selection is on a row, asked at the moment it matters.
   *
   * A predicate rather than a set, and for the same reason a create takes one:
   * an edit is decided twice -- once optimistically and once against what the
   * server answered -- and between the two the selection has usually moved. A
   * set captured when the edit was made says the row is still being looked at
   * long after somebody has clicked away, so the departure it is held for never
   * happens.
   *
   * Asked rather than inferred, because this layer has no business knowing what
   * a selection is: the grid knows, and a row modal editing the same row has a
   * different answer -- nothing there moves, so nothing there needs holding.
   */
  isHeld?: (rowId: number) => boolean
}

/**
 * Writes new values into one row.
 *
 * Rejects when the server refuses, after putting back what was there. What
 * happens to the row while the request is out is `applyOptimistically`'s
 * question.
 */
export async function updateRowValues(
  member: RowWindowMember,
  rowId: number,
  values: Record<string, unknown>,
  options: UpdateRowOptions = {},
): Promise<void> {
  await updateRows(member, [{ rowId, values }], options)
}

/**
 * Writes new values into several rows at once, as one request.
 *
 * A paste is why this exists. A request per row would be one round trip per
 * row, one undo entry per row, and one broadcast per row to everybody else
 * looking at the table -- for something the person did once.
 *
 * Queued under every row it touches, so a paste and a single-cell edit inside
 * it cannot overlap. The alternative -- a queue of its own, keyed by the table
 * -- is what upstream does, and it leaves exactly that race open.
 */
export async function updateRows(
  member: RowWindowMember,
  updates: readonly { rowId: number; values: Record<string, unknown> }[],
  { searchTerm = '', isHeld }: UpdateRowOptions = {},
): Promise<void> {
  if (updates.length === 0) return

  const previous: RowData[] = []
  for (const { rowId, values } of updates) {
    const before = applyOptimistically(
      member,
      rowId,
      values,
      searchTerm,
      isHeld?.(rowId) ?? false,
    )
    if (before !== undefined) previous.push(before)
  }

  const touched = updates.map(({ rowId }) => rowId)
  // Marked as waiting only while the client cannot say where the row belongs.
  //
  // The spinner is not "a request is out" -- one always is. It is "the answer
  // to where this row goes is not here yet", which is true exactly when a rule
  // the view applies reads a value the server computes. Where the client can
  // decide, it already has: the value is on screen and the badge is or is not,
  // and a spinner beside a decision that has been taken says the opposite of
  // what is true.
  const undecided = !canUpdateOptimistically(member.view(), member.fields(), searchTerm)
  if (undecided) {
    for (const rowId of touched) member.store.getState().setSaving(rowId, true)
  }

  try {
    await queue.run(touched, async () => {
      const body = await unwrap<BatchUpdateResponse>(
        getApi().PATCH('/api/database/rows/table/{table_id}/batch/', {
          params: {
            path: { table_id: member.tableId },
            // The view matters: it decides which default values apply and
            // which permission check runs, and the server needs to know an
            // edit came from one rather than from the API.
            query: { view: member.viewId, include_metadata: true },
          },
          // Cast for the same reason the response is hand-typed: the
          // generated request body is the fictional example table's, and no
          // real edit fits it.
          body: {
            // Resolved here rather than when the edit was made: a row created
            // a moment ago is edited under the temporary id it was given, and
            // the create that holds that key has finished by the time this
            // runs.
            items: updates.map(({ rowId, values }) => ({
              id: realId(rowId),
              ...values,
            })),
          } as never,
        }),
      )

      // A row the client changed can change rows it never touched -- a link
      // row updates the row at the other end of it, a formula elsewhere
      // recalculates. Those come back under `cascade_update`.
      const updated = [
        ...(body.items ?? []),
        ...(body.metadata?.cascade_update?.rows ?? []),
      ] as RowData[]

      for (const row of updated) {
        member.store.getState().updateRowInPlace(row)
      }
      // Where each row belongs is settled against the server's values, not the
      // typed ones. That is the whole of the deferred case: a view filtering or
      // sorting on a formula could not be asked before, and this is the moment
      // it can be. A row the edit never touched -- one that came back under
      // `cascade_update` -- is settled too, and nobody is on it, so it simply
      // goes where it now belongs.
      for (const row of updated) {
        settleRow(member, row.id, {
          confirmed: true,
          held: isHeld?.(row.id) ?? false,
        })
      }
    })
  } catch (error) {
    // Put back exactly what was there. Not a re-derivation from the values
    // sent: another event may have changed a row in the meantime, and the rows
    // as they were are the only thing this code knows to be true.
    for (const before of previous) {
      member.store.getState().updateRowInPlace(before)
    }
    // Discarded, not released: releasing applies the departure that was
    // waiting, and a row put back never departed.
    member.store.getState().discardHolds(touched)
    // The values are back, and so the row's place has to go back with them. A
    // row whose departure was *released* before the request failed has already
    // moved, and putting the old values into a row in its new place leaves the
    // view in an order neither the edit nor the table ever had. Settled rather
    // than restored to a remembered index, because the answer is knowable: the
    // values on the row are the server's own, from before the edit.
    for (const before of previous) {
      settleRow(member, before.id, { confirmed: true, held: false })
    }
    throw error
  } finally {
    if (undecided) {
      for (const rowId of touched) member.store.getState().setSaving(rowId, false)
    }
  }
}

/**
 * Shows an edit before the server has seen it, and reports what was there.
 *
 * The values always change immediately. Where the row goes is a separate
 * question, and one the client can only answer while every rule the view
 * applies reads a field it holds the value of. Where it cannot -- a sort over a
 * formula, a filter over a rollup -- the row stays where it is, and the
 * server's answer is what moves it.
 *
 * When the edit does move or remove the row, the departure is *held* rather
 * than applied: the row stays, badged, until the selection leaves it. Applying
 * it immediately is correct and unusable -- the row being typed into vanishes
 * under the cursor, or jumps out of view mid-keystroke and the next character
 * lands somewhere else.
 */
function applyOptimistically(
  member: RowWindowMember,
  rowId: number,
  values: Record<string, unknown>,
  searchTerm: string,
  hold: boolean,
): RowData | undefined {
  const before = member.store.getState().window.rows.find((row) => row.id === rowId)
  if (before === undefined) return undefined

  const after: RowData = { ...before, ...values }

  if (canUpdateOptimistically(member.view(), member.fields(), searchTerm)) {
    const operations = operationsFor(
      member.store.getState().window,
      rowUpdated(before, after, lifecycleContextFor(member)),
    )
    const { now, hold: held } = hold
      ? splitHold(operations)
      : { now: operations, hold: null }

    applyRowOperations(member.store, now)
    if (held !== null) {
      // A held row stays on screen, so it has to show what was typed into it.
      // The departure can be the whole of the answer -- a row the filters now
      // exclude is one `remove` and nothing else -- and holding that back
      // without applying the values leaves the row badged as not matching
      // while still showing the value that matched.
      member.store.getState().updateRowInPlace(after)
      member.store.getState().holdRow(rowId, held)
    } else {
      // No departure any more, so nothing to hold it for. This is the row that
      // was created outside the filters and has just been typed into until it
      // matches -- the badge has to go with the reason for it.
      member.store.getState().discardHolds([rowId])
    }
  } else {
    member.store.getState().updateRowInPlace(after)
  }

  return before
}

/**
 * Deletes rows.
 *
 * They go from the window immediately, because a row that stays on screen while
 * a request is out is a row somebody clicks again. Put back where they were if
 * the server refuses -- by index, not by re-sorting, since the view may have no
 * order the client could reproduce.
 *
 * Queued under every row it takes, so a delete cannot overlap an edit to one of
 * them. That ordering is the difference between "the edit was lost" and "the
 * row was deleted", which is a difference somebody will ask about.
 */
export async function deleteRows(
  member: RowWindowMember,
  rowIds: readonly number[],
): Promise<void> {
  if (rowIds.length === 0) return

  const held = member.store.getState().window
  // Recorded before anything is removed, because each removal shifts the ones
  // after it. Sorted so putting them back walks the same order it took them in.
  const removed = rowIds
    .map((rowId) => ({
      row: held.rows.find((r) => r.id === rowId),
      index: indexOfRow(held, rowId),
    }))
    .filter((entry): entry is { row: RowData; index: number } => entry.row !== undefined)
    .sort((a, b) => a.index - b.index)

  for (const rowId of rowIds) member.store.getState().removeRow(rowId)

  try {
    await queue.run(rowIds, async () => {
      await unwrap(
        getApi().POST('/api/database/rows/table/{table_id}/batch-delete/', {
          params: {
            path: { table_id: member.tableId },
            query: { view: member.viewId },
          },
          body: { items: rowIds.map(realId) },
        }),
      )
    })
  } catch (error) {
    for (const { row, index } of removed) {
      member.store.getState().insertRowAt(row, index)
    }
    throw error
  }
}

/* --------------------------------------------------------------- creating */

/**
 * The id a row has before the server has given it one.
 *
 * Negative and counting down, so it can never collide with a real id and so a
 * temporary row is recognisable on sight while debugging. Module-level because
 * the map below is: two grids on the same table each create rows, and an edit
 * has to resolve whichever id it was handed.
 */
let nextTemporaryId = -1

/**
 * Temporary ids to the ones the server gave them.
 *
 * This is what makes a row editable the moment it appears. The edit is queued
 * under the temporary id, so it waits for the create that holds that key; by
 * the time it runs, the create has resolved and put the real id here. Upstream
 * calls the same thing a persistent id and locks its queue by it.
 *
 * Entries are never removed. A row created and then edited an hour later still
 * resolves, and the map holds one small number pair per row created in this
 * tab's lifetime.
 */
const realIds = new Map<number, number>()

const realId = (rowId: number): number => realIds.get(rowId) ?? rowId

/**
 * Creates a row, showing it before the server has agreed to it.
 *
 * `before` is the row it goes above, or nothing for the end of the view. The
 * row appears immediately with a temporary id, which is what lets somebody
 * create a row and start typing into it without waiting for a round trip --
 * and it is why `realIds` exists.
 *
 * Rejects when the server refuses, after taking the row back off the screen.
 */
export async function createRow(
  member: RowWindowMember,
  {
    before,
    index,
    onIdResolved,
    isHeld,
  }: {
    before?: number
    index: number
    /**
     * Called when the server names the row.
     *
     * A new row appears immediately under a temporary id, and anything holding
     * that id -- the selection, most of all -- is pointing at a row that is
     * about to be called something else. This is how they find out.
     */
    onIdResolved?: (temporaryId: number, realId: number) => void
    /**
     * Whether the selection is on this row, asked at the moment it matters.
     *
     * A predicate rather than a set: a create is judged twice, and between the
     * two the selection has usually moved on. Defaults to held, which is the
     * answer for the row the grid has just selected.
     */
    isHeld?: (rowId: number) => boolean
  },
): Promise<void> {
  await createRows(member, {
    values: [{}],
    index,
    ...(before === undefined ? {} : { before }),
    ...(onIdResolved === undefined ? {} : { onIdResolved }),
    ...(isHeld === undefined ? {} : { isHeld }),
  })
}

/**
 * The most rows one request may carry.
 *
 * The backend's own limit, declared on the batch endpoints as `maxItems`. A
 * paste bigger than this is clipped rather than split: several requests would
 * be several undo entries and several broadcasts for one action, and a failure
 * halfway through would leave the table in a state nobody asked for.
 */
export const MAX_BATCH_ROWS = 200

/**
 * Creates several rows at once, with their values.
 *
 * The values go in the create rather than in a `PATCH` after it, so a paste
 * that grows the table is one request per half rather than one per row and a
 * second pass over the new ones.
 *
 * Rejects when the server refuses, after taking the rows back off the screen.
 */
/**
 * Decides where a row belongs now, and either does it or holds it.
 *
 * The one place a row's place is settled, whichever way the row got here: a
 * create the client has just put on screen, a create the server has just named,
 * or an edit the server has just answered. All three ask the same question --
 * does this row still belong in this view, and where -- and all three have the
 * same two answers depending on whether anybody is on it.
 *
 * `confirmed` says whether the values being judged are the server's. Until they
 * are, nothing is claimed about a row whose place the client cannot work out:
 * when the view sorts or filters on a field the server computes -- a formula, a
 * rollup -- the deciding values are not on the row yet, so a badge now would be
 * a guess. Once the answer is in, that gate would be wrong to apply, because
 * the row is carrying exactly those values.
 *
 * `held` says whether the selection is on the row. A row somebody is on stays
 * where it is and is badged; a row nobody is on simply goes where it belongs,
 * which is what a departure held for a selection that has since moved would
 * otherwise be waiting forever for.
 */
function settleRow(
  member: RowWindowMember,
  rowId: number,
  { confirmed, held }: { confirmed: boolean; held: boolean },
): void {
  const store = member.store.getState()
  const row = store.window.rows.find((candidate) => candidate?.id === rowId)
  if (row === undefined) return

  if (!confirmed && !canUpdateOptimistically(member.view(), member.fields(), '')) {
    store.discardHolds([rowId])
    return
  }

  const operations = operationsFor(
    store.window,
    rowSettled(row, lifecycleContextFor(member)),
  )
  const { now, hold } = held ? splitHold(operations) : { now: operations, hold: null }

  applyRowOperations(member.store, now)
  if (hold !== null) {
    store.holdRow(rowId, hold)
  } else {
    // Where it belongs after all -- so the badge goes with the reason for it.
    store.discardHolds([rowId])
  }
}

export async function createRows(
  member: RowWindowMember,
  {
    values,
    before,
    index,
    onIdResolved,
    isHeld,
  }: {
    values: readonly Record<string, unknown>[]
    before?: number
    index: number
    onIdResolved?: (temporaryId: number, realId: number) => void
    /**
     * Whether the selection is on this row, asked at the moment it matters.
     *
     * A predicate rather than a set: a create is decided twice, and between the
     * two the selection has usually moved. Defaults to held, which is the
     * answer for the row the grid has just selected.
     */
    isHeld?: (rowId: number) => boolean
  },
): Promise<void> {
  if (values.length === 0) return

  const temporaryIds = values.map(() => {
    const id = nextTemporaryId
    nextTemporaryId -= 1
    return id
  })

  // `order` is the backend's to decide -- it is an arbitrary-precision decimal
  // and the client cannot pick the one it will choose. But it cannot be left
  // out either, and it cannot be a constant: with no sorts on the view, `order`
  // *is* the ordering, so a placeholder of zero puts every new row at the top
  // of the window and every lifecycle decision then reads the row as having
  // moved. It is set from the neighbours it was inserted between instead, which
  // is the one thing about it the client does know.
  const surroundings = member.store.getState().window
  const orders = placeholderOrders(
    rowAt(surroundings, index - 1)?.order ?? null,
    rowAt(surroundings, index)?.order ?? null,
    values.length,
  )

  temporaryIds.forEach((id, offset) => {
    member.store
      .getState()
      .insertRowAt({ ...values[offset], id, order: orders[offset]! }, index + offset)
    // Held unconditionally on the way in. The row is selected by whoever asked
    // for it, and that happens after this returns -- so asking now would always
    // be told no, and the badge would never appear at all.
    settleRow(member, id, { confirmed: false, held: true })
  })

  // Marked under the temporary ids, which are what the rows are called until
  // the answer arrives -- and cleared under both, because `replaceRowId` has
  // renamed them by the time the request finishes.
  for (const id of temporaryIds) member.store.getState().setSaving(id, true)

  try {
    await queue.run(temporaryIds, async () => {
      const body = await unwrap<BatchUpdateResponse>(
        getApi().POST('/api/database/rows/table/{table_id}/batch/', {
          params: {
            path: { table_id: member.tableId },
            query: {
              view: member.viewId,
              ...(before === undefined ? {} : { before }),
            },
          },
          // Cast for the same reason the rest of this file casts: the generated
          // body is the fictional example table's.
          body: { items: values } as never,
        }),
      )

      const created = (body.items ?? []) as RowData[]
      if (created.length !== temporaryIds.length) {
        // A success carrying the wrong number of rows is not an answer. Raised
        // rather than shrugged off, so the placeholders come off the screen:
        // left behind, they are rows with ids that will never resolve, and
        // every later edit to one would go to the server as a row that does
        // not exist.
        throw new Error('The rows were created but not returned')
      }

      temporaryIds.forEach((temporaryId, offset) => {
        const row = created[offset]!
        realIds.set(temporaryId, row.id)
        // The hold moves across with the row: it is keyed by id, and the id
        // has just changed from the temporary one to the server's.
        member.store.getState().discardHolds([temporaryId])
        member.store.getState().replaceRowId(temporaryId, row)
        onIdResolved?.(temporaryId, row.id)
        settleRow(member, row.id, {
          confirmed: true,
          held: isHeld?.(row.id) ?? true,
        })
      })
    })
  } catch (error) {
    for (const id of temporaryIds) member.store.getState().removeRow(id)
    throw error
  } finally {
    for (const id of temporaryIds) {
      member.store.getState().setSaving(id, false)
      const real = realIds.get(id)
      if (real !== undefined) member.store.getState().setSaving(real, false)
    }
  }
}
